import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import { ROLES } from './config/Constants';

// Pages
import Login         from './pages/Login';
import Dashboard     from './pages/Dashboard';
import Users         from './pages/Users';
import Vehicles      from './pages/Vehicles';
import KYC           from './pages/KYC';
import Employees     from './pages/Employees';
import BatteryStations from './pages/BatteryStations';
import Complaints    from './pages/Complaints';
import Tickets       from './pages/Tickets';
import Notifications from './pages/Notifications';
import Reports       from './pages/Reports';
import Settings      from './pages/Settings';
import VehicleLocations from './pages/VehicleLocations';
import VehiclePathDetails from './pages/VehiclePathDetails';
import VehicleDetail from './pages/VehicleDetail';
import UserDetail from './pages/UserDetail';
import FleetTrackingReport from './pages/FleetTrackingReport';
import VehicleAssignmentReport from './pages/VehicleAssignmentReport';
import FleetMovementReport from './pages/FleetMovementReport';
import DeleteAccount from './pages/DeleteAccount';
import LoconavTracking from './pages/LoconavTracking';
import VehicleTrackList from './pages/VehicleTrackList';
import VehicleTrackDetail from './pages/VehicleTrackDetail';
import RoleManager from './pages/RoleManager';

// ── MUI Theme ────────────────────────────────────────────────────────────────
const theme = createTheme({
  palette: {
    primary:    { main: '#0A2540' },
    secondary:  { main: '#00C6FF' },
    background: { default: '#F4F7FB' },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  shape: { borderRadius: 8 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600 },
      },
    },
    MuiTextField: {
      defaultProps: { variant: 'outlined' },
    },
    MuiCard: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
      },
    },
  },
});

// ── Route guards ─────────────────────────────────────────────────────────────
const PrivateRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  // allowedRoles empty array = all roles allowed
  if (allowedRoles && allowedRoles.length > 0 && !allowedRoles.includes(user.role?.toLowerCase())) {
    return <Navigate to="/dashboard" replace />;
  }
  return <Layout>{children}</Layout>;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? <Navigate to="/dashboard" replace /> : children;
};

// ── App ───────────────────────────────────────────────────────────────────────
export default function App() {
  const ALL  = [];  // empty = all logged-in roles
  const SA_A = [ROLES.SUPERADMIN, ROLES.ADMIN];
  const MGR  = [ROLES.SUPERADMIN, ROLES.ADMIN, ROLES.MANAGER];
  const MGR_V= [ROLES.SUPERADMIN, ROLES.ADMIN, ROLES.MANAGER, ROLES.VENDOR];
  const SA   = [ROLES.SUPERADMIN];

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public */}
            <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
            <Route path="/delete-account" element={<DeleteAccount />} />

            {/* Private — all roles */}
            <Route path="/dashboard"    element={<PrivateRoute allowedRoles={ALL}><Dashboard /></PrivateRoute>} />
            <Route path="/stations"     element={<PrivateRoute allowedRoles={ALL}><BatteryStations /></PrivateRoute>} />
            <Route path="/complaints"   element={<PrivateRoute allowedRoles={ALL}><Complaints /></PrivateRoute>} />
            <Route path="/tickets"      element={<PrivateRoute allowedRoles={ALL}><Tickets /></PrivateRoute>} />
            <Route path="/notifications" element={<PrivateRoute allowedRoles={ALL}><Notifications /></PrivateRoute>} />
            <Route path="/settings"     element={<PrivateRoute allowedRoles={ALL}><Settings /></PrivateRoute>} />

            {/* Private — manager+ */}
            <Route path="/users"    element={<PrivateRoute allowedRoles={MGR}><Users /></PrivateRoute>} />
            <Route path="/user/:userId" element={<PrivateRoute allowedRoles={MGR}><UserDetail /></PrivateRoute>} />
            <Route path="/vehicles" element={<PrivateRoute allowedRoles={MGR_V}><Vehicles /></PrivateRoute>} />
            <Route path="/vehicle/:vehicleId" element={<PrivateRoute allowedRoles={MGR_V}><VehicleDetail /></PrivateRoute>} />
            <Route path="/tracking" element={<PrivateRoute allowedRoles={MGR}><LoconavTracking /></PrivateRoute>} />
            <Route path="/vehicle-track" element={<PrivateRoute allowedRoles={MGR}><VehicleTrackList /></PrivateRoute>} />
            <Route path="/vehicle-track/:uuid" element={<PrivateRoute allowedRoles={MGR}><VehicleTrackDetail /></PrivateRoute>} />
            <Route path="/locations" element={<PrivateRoute allowedRoles={MGR}><VehicleLocations /></PrivateRoute>} />
            <Route path="/vehicle-tracking" element={<PrivateRoute allowedRoles={MGR}><VehiclePathDetails /></PrivateRoute>} />
            <Route path="/vehicle-path/:vehicleId" element={<PrivateRoute allowedRoles={MGR}><VehiclePathDetails /></PrivateRoute>} />
            <Route path="/fleet-report" element={<PrivateRoute allowedRoles={MGR}><FleetTrackingReport /></PrivateRoute>} />
            <Route path="/report/vehicle-assignments" element={<PrivateRoute allowedRoles={MGR}><VehicleAssignmentReport /></PrivateRoute>} />
            <Route path="/report/fleet-movement" element={<PrivateRoute allowedRoles={MGR}><FleetMovementReport /></PrivateRoute>} />

            {/* Private — admin+ */}
            <Route path="/kyc"       element={<PrivateRoute allowedRoles={SA_A}><KYC /></PrivateRoute>} />
            <Route path="/employees" element={<PrivateRoute allowedRoles={SA_A}><Employees /></PrivateRoute>} />
            <Route path="/roles"     element={<PrivateRoute allowedRoles={SA}><RoleManager /></PrivateRoute>} />
            <Route path="/reports"   element={<PrivateRoute allowedRoles={SA_A}><Reports /></PrivateRoute>} />

            {/* Catch-all */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}
